// The meeting canvas between Stop and the finished meeting (PL-30): the quiet
// progress header, its four steps, the typical-duration estimate and the
// de-emphasised transcript underneath.
//
// Namespace `meetings-finishing`; owner: the PL-30 polish chunk
// (ui/workspace/meeting-finishing.tsx). Placeholders are `{name}`, filled with
// `formatCopy`. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  eyebrow: 'Finishing up',
  activityTranscript: 'Improving the transcript and recognising voices',
  activityVoices: 'Recognising voices',
  activityCorrections: 'Checking names and company terms',
  activityNotes: 'Writing the notes',
  stepsLabel: 'Progress',
  stepTranscript: 'Transcript',
  stepSpeakers: 'Speakers',
  stepCorrections: 'Corrections',
  stepNotes: 'Notes',
  stepDone: 'Done',
  stepActive: 'In progress',
  stepWaiting: 'Waiting',
  estimateMinutes: 'Usually about {minutes} min.',
  estimateUnderMinute: 'Usually under a minute.',
  estimateLonger: 'Taking longer than usual.',
  leaveNote: 'You can close this canvas. Inbox lets you know when the transcript is ready.',
  liveTranscript: 'Live transcript',
  transcript: 'Transcript',
  noLiveText: 'Nothing was transcribed live. The transcript comes from the recording.',
  showNow: 'Open the meeting now',
  showAllLines: 'Show all lines ({count})',
  stepPosition: '{step} · {position} of {total}',
};

export type MeetingsFinishingCopyKey = keyof typeof en;

const meetingsFinishingCopy: NamespaceCatalog<MeetingsFinishingCopyKey> = {
  bg: {
    eyebrow: 'Приключване',
    activityTranscript: 'Подобряваме транскрипцията и разпознаваме гласовете',
    activityVoices: 'Разпознаваме гласовете',
    activityCorrections: 'Проверяваме имената и фирмените термини',
    activityNotes: 'Пишем бележките',
    stepsLabel: 'Напредък',
    stepTranscript: 'Транскрипция',
    stepSpeakers: 'Говорители',
    stepCorrections: 'Корекции',
    stepNotes: 'Бележки',
    stepDone: 'Готово',
    stepActive: 'В процес',
    stepWaiting: 'Изчаква',
    estimateMinutes: 'Обикновено около {minutes} мин.',
    estimateUnderMinute: 'Обикновено под минута.',
    estimateLonger: 'Отнема повече време от обичайното.',
    leaveNote:
      'Можете да затворите този изглед. Входящата кутия ще ви уведоми, когато транскрипцията е готова.',
    liveTranscript: 'Транскрипция на живо',
    transcript: 'Транскрипция',
    noLiveText: 'Нищо не беше транскрибирано на живо. Транскрипцията се прави от записа.',
    showNow: 'Отворете срещата сега',
    showAllLines: 'Покажете всички редове ({count})',
    stepPosition: '{step} · {position} от {total}',
  },
  hr: {
    eyebrow: 'Završavanje',
    activityTranscript: 'Poboljšavamo transkript i prepoznajemo glasove',
    activityVoices: 'Prepoznajemo glasove',
    activityCorrections: 'Provjeravamo imena i pojmove tvrtke',
    activityNotes: 'Pišemo bilješke',
    stepsLabel: 'Napredak',
    stepTranscript: 'Transkript',
    stepSpeakers: 'Govornici',
    stepCorrections: 'Ispravci',
    stepNotes: 'Bilješke',
    stepDone: 'Gotovo',
    stepActive: 'U tijeku',
    stepWaiting: 'Na čekanju',
    estimateMinutes: 'Obično oko {minutes} min.',
    estimateUnderMinute: 'Obično manje od minute.',
    estimateLonger: 'Traje dulje nego inače.',
    leaveNote:
      'Možete zatvoriti ovaj prikaz. Pristigla pošta javit će vam kad transkript bude spreman.',
    liveTranscript: 'Transkript uživo',
    transcript: 'Transkript',
    noLiveText: 'Ništa nije transkribirano uživo. Transkript se izrađuje iz snimke.',
    showNow: 'Otvori sastanak sada',
    showAllLines: 'Prikaži sve retke ({count})',
    stepPosition: '{step} · {position} od {total}',
  },
  cs: {
    eyebrow: 'Dokončování',
    activityTranscript: 'Vylepšujeme přepis a rozpoznáváme hlasy',
    activityVoices: 'Rozpoznáváme hlasy',
    activityCorrections: 'Kontrolujeme jména a firemní pojmy',
    activityNotes: 'Píšeme poznámky',
    stepsLabel: 'Průběh',
    stepTranscript: 'Přepis',
    stepSpeakers: 'Mluvčí',
    stepCorrections: 'Opravy',
    stepNotes: 'Poznámky',
    stepDone: 'Hotovo',
    stepActive: 'Probíhá',
    stepWaiting: 'Čeká',
    estimateMinutes: 'Obvykle asi {minutes} min.',
    estimateUnderMinute: 'Obvykle méně než minuta.',
    estimateLonger: 'Trvá to déle než obvykle.',
    leaveNote: 'Toto zobrazení můžete zavřít. Doručená pošta vám dá vědět, až bude přepis hotový.',
    liveTranscript: 'Živý přepis',
    transcript: 'Přepis',
    noLiveText: 'Nic nebylo přepsáno živě. Přepis vznikne z nahrávky.',
    showNow: 'Otevřít schůzku hned',
    showAllLines: 'Zobrazit všechny řádky ({count})',
    stepPosition: '{step} · {position} z {total}',
  },
  da: {
    eyebrow: 'Afsluttes',
    activityTranscript: 'Forbedrer transskriptionen og genkender stemmer',
    activityVoices: 'Genkender stemmer',
    activityCorrections: 'Tjekker navne og virksomhedens begreber',
    activityNotes: 'Skriver noterne',
    stepsLabel: 'Fremskridt',
    stepTranscript: 'Transskription',
    stepSpeakers: 'Talere',
    stepCorrections: 'Rettelser',
    stepNotes: 'Noter',
    stepDone: 'Færdig',
    stepActive: 'I gang',
    stepWaiting: 'Venter',
    estimateMinutes: 'Tager normalt omkring {minutes} min.',
    estimateUnderMinute: 'Tager normalt under et minut.',
    estimateLonger: 'Det tager længere tid end normalt.',
    leaveNote: 'Du kan lukke denne visning. Indbakken giver besked, når transskriptionen er klar.',
    liveTranscript: 'Livetransskription',
    transcript: 'Transskription',
    noLiveText: 'Intet blev transskriberet live. Transskriptionen laves ud fra optagelsen.',
    showNow: 'Åbn mødet nu',
    showAllLines: 'Vis alle linjer ({count})',
    stepPosition: '{step} · {position} af {total}',
  },
  nl: {
    eyebrow: 'Bezig met afronden',
    activityTranscript: 'Het transcript verbeteren en stemmen herkennen',
    activityVoices: 'Stemmen herkennen',
    activityCorrections: 'Namen en bedrijfstermen controleren',
    activityNotes: 'De notities schrijven',
    stepsLabel: 'Voortgang',
    stepTranscript: 'Transcript',
    stepSpeakers: 'Sprekers',
    stepCorrections: 'Correcties',
    stepNotes: 'Notities',
    stepDone: 'Klaar',
    stepActive: 'Bezig',
    stepWaiting: 'Wacht',
    estimateMinutes: 'Duurt meestal ongeveer {minutes} min.',
    estimateUnderMinute: 'Duurt meestal minder dan een minuut.',
    estimateLonger: 'Dit duurt langer dan normaal.',
    leaveNote:
      'Je kunt dit venster sluiten. Je krijgt bericht in je inbox als het transcript klaar is.',
    liveTranscript: 'Live transcript',
    transcript: 'Transcript',
    noLiveText: 'Er is niets live uitgeschreven. Het transcript wordt gemaakt van de opname.',
    showNow: 'Vergadering nu openen',
    showAllLines: 'Alle regels tonen ({count})',
    stepPosition: '{step} · {position} van {total}',
  },
  en,
  et: {
    eyebrow: 'Lõpetamine',
    activityTranscript: 'Täiustame transkriptsiooni ja tuvastame hääli',
    activityVoices: 'Tuvastame hääli',
    activityCorrections: 'Kontrollime nimesid ja ettevõtte termineid',
    activityNotes: 'Kirjutame märkmeid',
    stepsLabel: 'Edenemine',
    stepTranscript: 'Transkriptsioon',
    stepSpeakers: 'Kõnelejad',
    stepCorrections: 'Parandused',
    stepNotes: 'Märkmed',
    stepDone: 'Valmis',
    stepActive: 'Käimas',
    stepWaiting: 'Ootel',
    estimateMinutes: 'Tavaliselt umbes {minutes} min.',
    estimateUnderMinute: 'Tavaliselt alla minuti.',
    estimateLonger: 'See võtab tavalisest kauem.',
    leaveNote: 'Võid selle vaate sulgeda. Postkast annab teada, kui transkriptsioon on valmis.',
    liveTranscript: 'Otsetranskriptsioon',
    transcript: 'Transkriptsioon',
    noLiveText: 'Otse ei transkribeeritud midagi. Transkriptsioon tehakse salvestisest.',
    showNow: 'Ava koosolek kohe',
    showAllLines: 'Näita kõiki ridu ({count})',
    stepPosition: '{step} · {position}/{total}',
  },
  fi: {
    eyebrow: 'Viimeistellään',
    activityTranscript: 'Parannetaan litteraatiota ja tunnistetaan ääniä',
    activityVoices: 'Tunnistetaan ääniä',
    activityCorrections: 'Tarkistetaan nimiä ja yrityksen termejä',
    activityNotes: 'Kirjoitetaan muistiinpanoja',
    stepsLabel: 'Eteneminen',
    stepTranscript: 'Litteraatio',
    stepSpeakers: 'Puhujat',
    stepCorrections: 'Korjaukset',
    stepNotes: 'Muistiinpanot',
    stepDone: 'Valmis',
    stepActive: 'Käynnissä',
    stepWaiting: 'Odottaa',
    estimateMinutes: 'Yleensä noin {minutes} min.',
    estimateUnderMinute: 'Yleensä alle minuutti.',
    estimateLonger: 'Tämä kestää tavallista kauemmin.',
    leaveNote: 'Voit sulkea tämän näkymän. Saapuneet-kansio kertoo, kun litteraatio on valmis.',
    liveTranscript: 'Reaaliaikainen litteraatio',
    transcript: 'Litteraatio',
    noLiveText: 'Mitään ei litteroitu reaaliajassa. Litteraatio tehdään tallenteesta.',
    showNow: 'Avaa kokous nyt',
    showAllLines: 'Näytä kaikki rivit ({count})',
    stepPosition: '{step} · {position}/{total}',
  },
  fr: {
    eyebrow: 'Finalisation',
    activityTranscript: 'Amélioration de la transcription et reconnaissance des voix',
    activityVoices: 'Reconnaissance des voix',
    activityCorrections: 'Vérification des noms et des termes de l’entreprise',
    activityNotes: 'Rédaction des notes',
    stepsLabel: 'Progression',
    stepTranscript: 'Transcription',
    stepSpeakers: 'Intervenants',
    stepCorrections: 'Corrections',
    stepNotes: 'Notes',
    stepDone: 'Terminé',
    stepActive: 'En cours',
    stepWaiting: 'En attente',
    estimateMinutes: 'Environ {minutes} min en général.',
    estimateUnderMinute: 'Moins d’une minute en général.',
    estimateLonger: 'Cela prend plus de temps que d’habitude.',
    leaveNote:
      'Vous pouvez fermer cette vue. Votre boîte de réception vous préviendra quand la transcription sera prête.',
    liveTranscript: 'Transcription en direct',
    transcript: 'Transcription',
    noLiveText:
      'Rien n’a été transcrit en direct. La transcription est réalisée à partir de l’enregistrement.',
    showNow: 'Ouvrir la réunion maintenant',
    showAllLines: 'Afficher toutes les lignes ({count})',
    stepPosition: '{step} · {position} sur {total}',
  },
  de: {
    eyebrow: 'Wird abgeschlossen',
    activityTranscript: 'Transkript wird verbessert und Stimmen werden erkannt',
    activityVoices: 'Stimmen werden erkannt',
    activityCorrections: 'Namen und Firmenbegriffe werden geprüft',
    activityNotes: 'Notizen werden geschrieben',
    stepsLabel: 'Fortschritt',
    stepTranscript: 'Transkript',
    stepSpeakers: 'Sprecher',
    stepCorrections: 'Korrekturen',
    stepNotes: 'Notizen',
    stepDone: 'Fertig',
    stepActive: 'Läuft',
    stepWaiting: 'Wartet',
    estimateMinutes: 'Dauert meist etwa {minutes} Min.',
    estimateUnderMinute: 'Dauert meist unter einer Minute.',
    estimateLonger: 'Das dauert länger als üblich.',
    leaveNote:
      'Sie können diese Ansicht schließen. Ihr Posteingang meldet sich, sobald das Transkript fertig ist.',
    liveTranscript: 'Live-Transkript',
    transcript: 'Transkript',
    noLiveText: 'Live wurde nichts transkribiert. Das Transkript entsteht aus der Aufnahme.',
    showNow: 'Meeting jetzt öffnen',
    showAllLines: 'Alle Zeilen anzeigen ({count})',
    stepPosition: '{step} · {position} von {total}',
  },
  el: {
    eyebrow: 'Ολοκλήρωση',
    activityTranscript: 'Βελτιώνουμε την απομαγνητοφώνηση και αναγνωρίζουμε φωνές',
    activityVoices: 'Αναγνωρίζουμε φωνές',
    activityCorrections: 'Ελέγχουμε ονόματα και όρους της εταιρείας',
    activityNotes: 'Γράφουμε τις σημειώσεις',
    stepsLabel: 'Πρόοδος',
    stepTranscript: 'Απομαγνητοφώνηση',
    stepSpeakers: 'Ομιλητές',
    stepCorrections: 'Διορθώσεις',
    stepNotes: 'Σημειώσεις',
    stepDone: 'Ολοκληρώθηκε',
    stepActive: 'Σε εξέλιξη',
    stepWaiting: 'Σε αναμονή',
    estimateMinutes: 'Συνήθως περίπου {minutes} λεπτά.',
    estimateUnderMinute: 'Συνήθως λιγότερο από ένα λεπτό.',
    estimateLonger: 'Διαρκεί περισσότερο από το συνηθισμένο.',
    leaveNote:
      'Μπορείτε να κλείσετε αυτή την προβολή. Τα εισερχόμενα θα σας ενημερώσουν όταν η απομαγνητοφώνηση είναι έτοιμη.',
    liveTranscript: 'Ζωντανή απομαγνητοφώνηση',
    transcript: 'Απομαγνητοφώνηση',
    noLiveText:
      'Τίποτα δεν απομαγνητοφωνήθηκε ζωντανά. Η απομαγνητοφώνηση γίνεται από την εγγραφή.',
    showNow: 'Ανοίξτε τη σύσκεψη τώρα',
    showAllLines: 'Εμφάνιση όλων των γραμμών ({count})',
    stepPosition: '{step} · {position} από {total}',
  },
  hu: {
    eyebrow: 'Befejezés',
    activityTranscript: 'Javítjuk az átiratot és felismerjük a hangokat',
    activityVoices: 'Felismerjük a hangokat',
    activityCorrections: 'Ellenőrizzük a neveket és a céges kifejezéseket',
    activityNotes: 'Megírjuk a jegyzeteket',
    stepsLabel: 'Haladás',
    stepTranscript: 'Átirat',
    stepSpeakers: 'Beszélők',
    stepCorrections: 'Javítások',
    stepNotes: 'Jegyzetek',
    stepDone: 'Kész',
    stepActive: 'Folyamatban',
    stepWaiting: 'Várakozik',
    estimateMinutes: 'Általában körülbelül {minutes} perc.',
    estimateUnderMinute: 'Általában kevesebb mint egy perc.',
    estimateLonger: 'A szokásosnál tovább tart.',
    leaveNote:
      'Ezt a nézetet bezárhatja. A beérkezett üzenetek között értesítjük, ha kész az átirat.',
    liveTranscript: 'Élő átirat',
    transcript: 'Átirat',
    noLiveText: 'Élőben semmit sem írtunk át. Az átirat a felvételből készül.',
    showNow: 'Megbeszélés megnyitása most',
    showAllLines: 'Összes sor megjelenítése ({count})',
    stepPosition: '{step} · {position}/{total}',
  },
  ga: {
    eyebrow: 'Á chríochnú',
    activityTranscript: 'Ag feabhsú an tras-scríbhinn agus ag aithint guthanna',
    activityVoices: 'Ag aithint guthanna',
    activityCorrections: 'Ag seiceáil ainmneacha agus téarmaí na cuideachta',
    activityNotes: 'Ag scríobh na nótaí',
    stepsLabel: 'Dul chun cinn',
    stepTranscript: 'Tras-scríbhinn',
    stepSpeakers: 'Cainteoirí',
    stepCorrections: 'Ceartúcháin',
    stepNotes: 'Nótaí',
    stepDone: 'Déanta',
    stepActive: 'Ar siúl',
    stepWaiting: 'Ag fanacht',
    estimateMinutes: 'Thart ar {minutes} nóiméad de ghnáth.',
    estimateUnderMinute: 'Níos lú ná nóiméad de ghnáth.',
    estimateLonger: 'Tá sé ag tógáil níos faide ná mar is gnách.',
    leaveNote:
      'Is féidir leat an t-amharc seo a dhúnadh. Cuirfidh an Bosca Isteach in iúl duit nuair a bheidh an tras-scríbhinn réidh.',
    liveTranscript: 'Tras-scríbhinn bheo',
    transcript: 'Tras-scríbhinn',
    noLiveText: 'Níor tras-scríobhadh aon rud beo. Déantar an tras-scríbhinn ón taifeadadh.',
    showNow: 'Oscail an cruinniú anois',
    showAllLines: 'Taispeáin gach líne ({count})',
    stepPosition: '{step} · {position} as {total}',
  },
  it: {
    eyebrow: 'Completamento',
    activityTranscript: 'Miglioriamo la trascrizione e riconosciamo le voci',
    activityVoices: 'Riconosciamo le voci',
    activityCorrections: 'Controlliamo nomi e termini aziendali',
    activityNotes: 'Scriviamo le note',
    stepsLabel: 'Avanzamento',
    stepTranscript: 'Trascrizione',
    stepSpeakers: 'Interlocutori',
    stepCorrections: 'Correzioni',
    stepNotes: 'Note',
    stepDone: 'Fatto',
    stepActive: 'In corso',
    stepWaiting: 'In attesa',
    estimateMinutes: 'Di solito circa {minutes} min.',
    estimateUnderMinute: 'Di solito meno di un minuto.',
    estimateLonger: 'Ci vuole più del solito.',
    leaveNote:
      'Puoi chiudere questa vista. La posta in arrivo ti avvisa quando la trascrizione è pronta.',
    liveTranscript: 'Trascrizione dal vivo',
    transcript: 'Trascrizione',
    noLiveText: 'Non è stato trascritto nulla dal vivo. La trascrizione viene dalla registrazione.',
    showNow: 'Apri subito la riunione',
    showAllLines: 'Mostra tutte le righe ({count})',
    stepPosition: '{step} · {position} di {total}',
  },
  lv: {
    eyebrow: 'Pabeidz',
    activityTranscript: 'Uzlabojam transkripciju un atpazīstam balsis',
    activityVoices: 'Atpazīstam balsis',
    activityCorrections: 'Pārbaudām vārdus un uzņēmuma terminus',
    activityNotes: 'Rakstām piezīmes',
    stepsLabel: 'Progress',
    stepTranscript: 'Transkripcija',
    stepSpeakers: 'Runātāji',
    stepCorrections: 'Labojumi',
    stepNotes: 'Piezīmes',
    stepDone: 'Gatavs',
    stepActive: 'Notiek',
    stepWaiting: 'Gaida',
    estimateMinutes: 'Parasti aptuveni {minutes} min.',
    estimateUnderMinute: 'Parasti mazāk par minūti.',
    estimateLonger: 'Tas aizņem ilgāku laiku nekā parasti.',
    leaveNote: 'Varat aizvērt šo skatu. Iesūtne jums paziņos, kad transkripcija būs gatava.',
    liveTranscript: 'Tiešraides transkripcija',
    transcript: 'Transkripcija',
    noLiveText: 'Tiešraidē nekas netika transkribēts. Transkripcija tiek veidota no ieraksta.',
    showNow: 'Atvērt sapulci tagad',
    showAllLines: 'Rādīt visas rindas ({count})',
    stepPosition: '{step} · {position} no {total}',
  },
  lt: {
    eyebrow: 'Užbaigiama',
    activityTranscript: 'Tobuliname stenogramą ir atpažįstame balsus',
    activityVoices: 'Atpažįstame balsus',
    activityCorrections: 'Tikriname vardus ir įmonės terminus',
    activityNotes: 'Rašome užrašus',
    stepsLabel: 'Eiga',
    stepTranscript: 'Stenograma',
    stepSpeakers: 'Kalbėtojai',
    stepCorrections: 'Pataisymai',
    stepNotes: 'Užrašai',
    stepDone: 'Atlikta',
    stepActive: 'Vyksta',
    stepWaiting: 'Laukia',
    estimateMinutes: 'Paprastai apie {minutes} min.',
    estimateUnderMinute: 'Paprastai mažiau nei minutė.',
    estimateLonger: 'Užtrunka ilgiau nei įprastai.',
    leaveNote: 'Galite uždaryti šį rodinį. Gautieji praneš, kai stenograma bus paruošta.',
    liveTranscript: 'Tiesioginė stenograma',
    transcript: 'Stenograma',
    noLiveText: 'Tiesiogiai nieko nebuvo transkribuota. Stenograma daroma iš įrašo.',
    showNow: 'Atidaryti susitikimą dabar',
    showAllLines: 'Rodyti visas eilutes ({count})',
    stepPosition: '{step} · {position} iš {total}',
  },
  mt: {
    eyebrow: 'Qed nispiċċaw',
    activityTranscript: 'Qed intejbu t-traskrizzjoni u nagħrfu l-vuċijiet',
    activityVoices: 'Qed nagħrfu l-vuċijiet',
    activityCorrections: 'Qed niċċekkjaw l-ismijiet u t-termini tal-kumpanija',
    activityNotes: 'Qed niktbu n-noti',
    stepsLabel: 'Progress',
    stepTranscript: 'Traskrizzjoni',
    stepSpeakers: 'Kelliema',
    stepCorrections: 'Korrezzjonijiet',
    stepNotes: 'Noti',
    stepDone: 'Lest',
    stepActive: 'Għaddej',
    stepWaiting: 'Qed jistenna',
    estimateMinutes: 'Normalment madwar {minutes} min.',
    estimateUnderMinute: 'Normalment inqas minn minuta.',
    estimateLonger: 'Qed jieħu aktar mis-soltu.',
    leaveNote: 'Tista’ tagħlaq din il-veduta. L-Inbox javżak meta t-traskrizzjoni tkun lesta.',
    liveTranscript: 'Traskrizzjoni diretta',
    transcript: 'Traskrizzjoni',
    noLiveText: 'Xejn ma ġie traskritt dirett. It-traskrizzjoni ssir mir-reġistrazzjoni.',
    showNow: 'Iftaħ il-laqgħa issa',
    showAllLines: 'Uri l-linji kollha ({count})',
    stepPosition: '{step} · {position} minn {total}',
  },
  pl: {
    eyebrow: 'Kończenie',
    activityTranscript: 'Poprawiamy transkrypcję i rozpoznajemy głosy',
    activityVoices: 'Rozpoznajemy głosy',
    activityCorrections: 'Sprawdzamy nazwy i terminy firmowe',
    activityNotes: 'Piszemy notatki',
    stepsLabel: 'Postęp',
    stepTranscript: 'Transkrypcja',
    stepSpeakers: 'Mówcy',
    stepCorrections: 'Poprawki',
    stepNotes: 'Notatki',
    stepDone: 'Gotowe',
    stepActive: 'W toku',
    stepWaiting: 'Czeka',
    estimateMinutes: 'Zwykle około {minutes} min.',
    estimateUnderMinute: 'Zwykle poniżej minuty.',
    estimateLonger: 'Trwa to dłużej niż zwykle.',
    leaveNote:
      'Możesz zamknąć ten widok. Skrzynka odbiorcza powiadomi Cię, gdy transkrypcja będzie gotowa.',
    liveTranscript: 'Transkrypcja na żywo',
    transcript: 'Transkrypcja',
    noLiveText: 'Nic nie zostało przepisane na żywo. Transkrypcja powstaje z nagrania.',
    showNow: 'Otwórz spotkanie teraz',
    showAllLines: 'Pokaż wszystkie wiersze ({count})',
    stepPosition: '{step} · {position} z {total}',
  },
  pt: {
    eyebrow: 'A concluir',
    activityTranscript: 'A melhorar a transcrição e a reconhecer vozes',
    activityVoices: 'A reconhecer vozes',
    activityCorrections: 'A verificar nomes e termos da empresa',
    activityNotes: 'A escrever as notas',
    stepsLabel: 'Progresso',
    stepTranscript: 'Transcrição',
    stepSpeakers: 'Oradores',
    stepCorrections: 'Correções',
    stepNotes: 'Notas',
    stepDone: 'Concluído',
    stepActive: 'Em curso',
    stepWaiting: 'Em espera',
    estimateMinutes: 'Normalmente cerca de {minutes} min.',
    estimateUnderMinute: 'Normalmente menos de um minuto.',
    estimateLonger: 'Está a demorar mais do que o habitual.',
    leaveNote:
      'Pode fechar esta vista. A caixa de entrada avisa quando a transcrição estiver pronta.',
    liveTranscript: 'Transcrição em direto',
    transcript: 'Transcrição',
    noLiveText: 'Nada foi transcrito em direto. A transcrição é feita a partir da gravação.',
    showNow: 'Abrir a reunião agora',
    showAllLines: 'Mostrar todas as linhas ({count})',
    stepPosition: '{step} · {position} de {total}',
  },
  ro: {
    eyebrow: 'Se finalizează',
    activityTranscript: 'Îmbunătățim transcrierea și recunoaștem vocile',
    activityVoices: 'Recunoaștem vocile',
    activityCorrections: 'Verificăm numele și termenii companiei',
    activityNotes: 'Scriem notele',
    stepsLabel: 'Progres',
    stepTranscript: 'Transcriere',
    stepSpeakers: 'Vorbitori',
    stepCorrections: 'Corecturi',
    stepNotes: 'Note',
    stepDone: 'Gata',
    stepActive: 'În curs',
    stepWaiting: 'În așteptare',
    estimateMinutes: 'De obicei, aproximativ {minutes} min.',
    estimateUnderMinute: 'De obicei, sub un minut.',
    estimateLonger: 'Durează mai mult decât de obicei.',
    leaveNote: 'Puteți închide această vizualizare. Inboxul vă anunță când transcrierea este gata.',
    liveTranscript: 'Transcriere live',
    transcript: 'Transcriere',
    noLiveText: 'Nimic nu a fost transcris live. Transcrierea se face din înregistrare.',
    showNow: 'Deschideți ședința acum',
    showAllLines: 'Afișați toate rândurile ({count})',
    stepPosition: '{step} · {position} din {total}',
  },
  sk: {
    eyebrow: 'Dokončovanie',
    activityTranscript: 'Vylepšujeme prepis a rozpoznávame hlasy',
    activityVoices: 'Rozpoznávame hlasy',
    activityCorrections: 'Kontrolujeme mená a firemné pojmy',
    activityNotes: 'Píšeme poznámky',
    stepsLabel: 'Priebeh',
    stepTranscript: 'Prepis',
    stepSpeakers: 'Rečníci',
    stepCorrections: 'Opravy',
    stepNotes: 'Poznámky',
    stepDone: 'Hotovo',
    stepActive: 'Prebieha',
    stepWaiting: 'Čaká',
    estimateMinutes: 'Zvyčajne asi {minutes} min.',
    estimateUnderMinute: 'Zvyčajne menej ako minúta.',
    estimateLonger: 'Trvá to dlhšie ako zvyčajne.',
    leaveNote:
      'Toto zobrazenie môžete zavrieť. Doručená pošta vám dá vedieť, keď bude prepis hotový.',
    liveTranscript: 'Živý prepis',
    transcript: 'Prepis',
    noLiveText: 'Nič nebolo prepísané naživo. Prepis vznikne z nahrávky.',
    showNow: 'Otvoriť stretnutie hneď',
    showAllLines: 'Zobraziť všetky riadky ({count})',
    stepPosition: '{step} · {position} z {total}',
  },
  sl: {
    eyebrow: 'Zaključevanje',
    activityTranscript: 'Izboljšujemo prepis in prepoznavamo glasove',
    activityVoices: 'Prepoznavamo glasove',
    activityCorrections: 'Preverjamo imena in izraze podjetja',
    activityNotes: 'Pišemo zapiske',
    stepsLabel: 'Napredek',
    stepTranscript: 'Prepis',
    stepSpeakers: 'Govorci',
    stepCorrections: 'Popravki',
    stepNotes: 'Zapiski',
    stepDone: 'Končano',
    stepActive: 'Poteka',
    stepWaiting: 'Čaka',
    estimateMinutes: 'Običajno približno {minutes} min.',
    estimateUnderMinute: 'Običajno manj kot minuta.',
    estimateLonger: 'Traja dlje kot običajno.',
    leaveNote: 'Ta pogled lahko zaprete. Prejeto vas bo obvestilo, ko bo prepis pripravljen.',
    liveTranscript: 'Prepis v živo',
    transcript: 'Prepis',
    noLiveText: 'V živo ni bilo nič prepisano. Prepis nastane iz posnetka.',
    showNow: 'Odprite sestanek zdaj',
    showAllLines: 'Pokaži vse vrstice ({count})',
    stepPosition: '{step} · {position} od {total}',
  },
  es: {
    eyebrow: 'Terminando',
    activityTranscript: 'Mejorando la transcripción y reconociendo voces',
    activityVoices: 'Reconociendo voces',
    activityCorrections: 'Comprobando nombres y términos de la empresa',
    activityNotes: 'Escribiendo las notas',
    stepsLabel: 'Progreso',
    stepTranscript: 'Transcripción',
    stepSpeakers: 'Hablantes',
    stepCorrections: 'Correcciones',
    stepNotes: 'Notas',
    stepDone: 'Hecho',
    stepActive: 'En curso',
    stepWaiting: 'En espera',
    estimateMinutes: 'Suele tardar unos {minutes} min.',
    estimateUnderMinute: 'Suele tardar menos de un minuto.',
    estimateLonger: 'Está tardando más de lo habitual.',
    leaveNote:
      'Puedes cerrar esta vista. La bandeja de entrada te avisará cuando la transcripción esté lista.',
    liveTranscript: 'Transcripción en directo',
    transcript: 'Transcripción',
    noLiveText:
      'No se transcribió nada en directo. La transcripción se hace a partir de la grabación.',
    showNow: 'Abrir la reunión ahora',
    showAllLines: 'Mostrar todas las líneas ({count})',
    stepPosition: '{step} · {position} de {total}',
  },
  sv: {
    eyebrow: 'Färdigställs',
    activityTranscript: 'Förbättrar transkriptet och känner igen röster',
    activityVoices: 'Känner igen röster',
    activityCorrections: 'Kontrollerar namn och företagets termer',
    activityNotes: 'Skriver anteckningarna',
    stepsLabel: 'Förlopp',
    stepTranscript: 'Transkript',
    stepSpeakers: 'Talare',
    stepCorrections: 'Rättelser',
    stepNotes: 'Anteckningar',
    stepDone: 'Klart',
    stepActive: 'Pågår',
    stepWaiting: 'Väntar',
    estimateMinutes: 'Brukar ta ungefär {minutes} min.',
    estimateUnderMinute: 'Brukar ta under en minut.',
    estimateLonger: 'Det tar längre tid än vanligt.',
    leaveNote: 'Du kan stänga den här vyn. Inkorgen meddelar dig när transkriptet är klart.',
    liveTranscript: 'Livetranskript',
    transcript: 'Transkript',
    noLiveText: 'Inget transkriberades live. Transkriptet görs från inspelningen.',
    showNow: 'Öppna mötet nu',
    showAllLines: 'Visa alla rader ({count})',
    stepPosition: '{step} · {position} av {total}',
  },
};
export default meetingsFinishingCopy;
